/**
 * @file OutputReportPollingExchange.ts
 * @description output report 发送 + feature report 轮询的交互策略。
 * 这是当前多数设备正在使用的旧协议模式，从 BaseDevice 中抽出后可被协议层复用。
 */

import { DeviceError, DeviceErrorCode } from '../../../errors/DeviceError'
import { delay } from '../../../utils/TimeUtils'
import type { HidChannel } from '../HidChannel'
import type { CommandExchange, CommandExchangeRequest } from './CommandExchange'

export interface OutputReportPollingExchangeOptions {
  getDelayMs?: () => number
  queue?: <T>(task: () => Promise<T>) => Promise<T>
}

/**
 * output report + feature report 轮询策略。
 */
export class OutputReportPollingExchange implements CommandExchange {
  constructor(
    private readonly channel: HidChannel,
    private readonly options: OutputReportPollingExchangeOptions = {},
  ) {}

  public async request(request: CommandExchangeRequest): Promise<DataView> {
    const run = async () => this.execute(request)
    return this.options.queue ? this.options.queue(run) : run()
  }

  private async execute(request: CommandExchangeRequest): Promise<DataView> {
    const timeout = request.timeout ?? 3000
    const maxRetries = request.maxRetries ?? 5
    const responseReportId = request.responseReportId ?? request.reportId

    await this.channel.sendOutputReport(request.reportId, request.payload)

    const startTime = Date.now()
    let retries = 0

    while (retries < maxRetries) {
      if (Date.now() - startTime > timeout) {
        throw new DeviceError(DeviceErrorCode.TIMEOUT, `Read timeout after ${timeout}ms`)
      }

      try {
        // 关键：有线/无线的最短等待时间由设备运行态传入，协议策略只负责按该规则执行。
        await delay(this.options.getDelayMs?.() ?? 1)
        const data = await this.channel.getFeatureReport(responseReportId)

        if (!request.validate || request.validate(data)) {
          return data
        }

        await delay(100)
        retries++
      } catch (error) {
        if (retries === maxRetries - 1) {
          throw error
        }
        retries++
      }
    }

    throw new DeviceError(DeviceErrorCode.TIMEOUT, `Max retries (${maxRetries}) reached`)
  }
}
